import React, { useState, useMemo, useEffect } from 'react';
import db from '../lib/database';
import { useStore } from '../store/StoreContext';
import { useToast } from '../hooks/useToast';
import { lookupRate, lookupRateInfo, lookupPackingRate } from '../utils/';
import { TRUCK_TYPE_OPTIONS_COMPACT, TRUCK_EXTRA_SIZES, extraTruckFeePerHrFrom, truckSizeShortLabel } from '../lib/constants';
import { TimePicker } from './ui';
import { computeDayHours } from '../lib/database';

// ============================================================================
// QuickPricer — a scratch-pad price, not attached to any client (Jaylen
// 2026-09-11: "select crew/truck and create a price… to use daily when
// forecasting price"). Lives on the Estimates page behind a Quick Price
// toggle. Same math as ClientPricingTab's LOCAL tab — rate table lookup
// by crew + truck, extra-truck surcharge on every billed hour, packing at
// the packing-table rate, flat add-ons (travel / hold / materials / heavy
// / removal) — so a number quoted from here matches what the estimate
// will say once the client file exists. Nothing is saved; the last
// inputs stick in localStorage so the widget opens the way you left it.
// ============================================================================

const LS_KEY = 'bbm.quickPricer.v1';
const CREW_SIZES = [2, 3, 4, 5, 6, 7, 8];
// Whole dollars for forecasts; cents when the math produces them (a
// 7.25-hour clock bill at $235 is $1,703.75, not $1,704).
const money = (n) => {
    const v = Math.round((Number(n) || 0) * 100) / 100;
    return Number.isInteger(v) ? `$${v.toLocaleString()}` : `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const num = (v) => parseFloat(v) || 0;

const DEFAULTS = {
    crew: 2, truck: '26ft', extras: [], waived: false,
    rate: '', rateTouched: false, minHours: '', maxHours: '',
    // Hours source (Jaylen 2026-09-11): 'range' = min/max forecast; 'clock'
    // = start / cut time − break, for billing a finished job from the
    // crew lead's times. 15-min increments, same math as the crew day-hours
    // feature (computeDayHours).
    hoursMode: 'range', clockIn: '08:30', clockOut: '16:15', breakMin: 30,
    packing: false, packers: 2, packRate: '', packRateTouched: false, packMin: '', packMax: '',
    packHoursMode: 'range', packClockIn: '08:30', packClockOut: '12:00', packBreakMin: 0,
    travel: '', hold: '', materials: '', heavy: 0, removal: '',
    // Card fee (Jaylen 2026-09-19: "add to the Billing calc a option to show
    // 3% credit card processing fee"). Off by default; when on, the readout
    // adds the fee line and a "paid by card" total under the cash total.
    cardFee: false,
    // Materials picks: { [material_type_id]: count } — the field shows the
    // taxed total; the picker (same catalog the crew portal uses) fills it.
    materialsPicks: {},
};

function loadSaved() {
    try {
        const raw = localStorage.getItem(LS_KEY);
        if (!raw) return DEFAULTS;
        const s = JSON.parse(raw);
        return { ...DEFAULTS, ...s, extras: Array.isArray(s.extras) ? s.extras : [], materialsPicks: s.materialsPicks && typeof s.materialsPicks === 'object' ? s.materialsPicks : {} };
    } catch { return DEFAULTS; }
}

// Presentational bits — module-level so their identity is stable.
const Label = ({ children }) => <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-1.5">{children}</div>;
const Hint = ({ children }) => <div className="font-mono text-[10px] text-slate-500 mt-1">{children}</div>;

const key = (active, extra = '') =>
    `h-9 min-w-[2.6rem] px-3 rounded-md text-[13px] font-bold tracking-tight transition-colors border-b-2 ${
        active
            ? 'bg-white text-slate-900 border-red-600 shadow-[inset_0_1px_0_rgba(255,255,255,.9)]'
            : 'bg-[#272b34] text-slate-200 border-[#0d0f13] hover:bg-[#323845] shadow-[inset_0_1px_0_rgba(255,255,255,.06)]'
    } ${extra}`;
const display = 'w-full h-9 bg-[#0e1014] border border-[#2c313b] rounded-md px-2.5 text-[15px] font-mono tabular-nums text-right text-white placeholder:text-slate-600 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/40';

// NumField — every number in the calculator (Jaylen 2026-09-11: the
// browser's native spinner arrows are tiny and flaky; "Lisa in her mid
// 60s" needs real targets). A typed field plus full-height − / + keys
// (36px each). Text input with inputMode so phones show a number pad but
// no native spinner ever renders. Select-all on focus: tap, type, done.
const NumField = ({ value, onChange, step = 1, min = 0, placeholder = '0', prefix, ariaLabel, decimals = 2 }) => {
    const cur = parseFloat(value);
    const base = Number.isFinite(cur) ? cur : 0;
    const clamp = (n) => Math.max(min, Math.round(n * 100) / 100);
    const fmt = (n) => Number.isInteger(n) ? String(n) : String(Number(n.toFixed(decimals)));
    return (
        <div className="flex items-stretch h-9 rounded-md overflow-hidden border border-[#2c313b] bg-[#0e1014] focus-within:border-red-500 focus-within:ring-1 focus-within:ring-red-500/40">
            {prefix && <span className="pl-2.5 self-center font-mono text-slate-500 text-[13px]">{prefix}</span>}
            <input
                type="text" inputMode="decimal"
                value={value ?? ''} placeholder={placeholder} aria-label={ariaLabel}
                onChange={e => onChange(e.target.value.replace(/[^0-9.]/g, ''))}
                onFocus={e => e.target.select()}
                className="flex-1 min-w-0 w-full bg-transparent px-2 font-mono text-[15px] tabular-nums text-right text-white placeholder:text-slate-600 focus:outline-none"
            />
            <button type="button" tabIndex={-1} onClick={() => onChange(fmt(clamp(base - step)))} aria-label={`Decrease${ariaLabel ? ` ${ariaLabel}` : ''}`}
                className="w-9 flex-shrink-0 border-l border-[#2c313b] text-slate-300 hover:bg-[#272b34] active:bg-[#323845] font-bold text-[16px] leading-none">−</button>
            <button type="button" tabIndex={-1} onClick={() => onChange(fmt(clamp(base + step)))} aria-label={`Increase${ariaLabel ? ` ${ariaLabel}` : ''}`}
                className="w-9 flex-shrink-0 border-l border-[#2c313b] text-slate-300 hover:bg-[#272b34] active:bg-[#323845] font-bold text-[16px] leading-none">+</button>
        </div>
    );
};

// Hours controls (Range | Clock + fields), shared by the labor row and
// the packing row so both work identically. `k` maps the generic field
// names onto this row's state keys.
// Module-level (stable identity) — defined inside the component it would
// remount its inputs on every keystroke.
const HoursControls = ({ s, set, k, calc, hours, minPh = '4', maxPh = '6' }) => (
    <>
        <div>
            <Label>Hours by</Label>
            <div className="inline-flex rounded-md overflow-hidden ring-1 ring-white/10">
                <button type="button" onClick={() => set({ [k.mode]: 'range' })} className={`h-9 px-3 text-[12px] font-bold transition-colors ${s[k.mode] === 'range' ? 'bg-white text-slate-900' : 'bg-[#272b34] text-slate-300 hover:bg-[#323845]'}`}>Range</button>
                <button type="button" onClick={() => set({ [k.mode]: 'clock' })} className={`h-9 px-3 text-[12px] font-bold transition-colors border-l border-white/10 ${s[k.mode] === 'clock' ? 'bg-white text-slate-900' : 'bg-[#272b34] text-slate-300 hover:bg-[#323845]'}`}>Clock</button>
            </div>
        </div>
        <div className="col-span-2 md:col-span-1">
            {s[k.mode] === 'range' ? (
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label>Min hrs</Label>
                        <NumField step={0.5} value={s[k.min]} onChange={v => set({ [k.min]: v })} placeholder={minPh} ariaLabel="min hours" />
                    </div>
                    <div>
                        <Label>Max hrs</Label>
                        <NumField step={0.5} value={s[k.max]} onChange={v => set({ [k.max]: v })} placeholder={maxPh} ariaLabel="max hours" />
                    </div>
                </div>
            ) : (
                <div className="flex flex-wrap items-start gap-3">
                    <div>
                        <Label>Start</Label>
                        <div className="h-9 flex items-center bg-white rounded-md px-1.5"><TimePicker compact value={s[k.in]} onChange={v => set({ [k.in]: v })} ariaLabel="Start time" /></div>
                    </div>
                    <div>
                        <Label>Cut</Label>
                        <div className="h-9 flex items-center bg-white rounded-md px-1.5"><TimePicker compact value={s[k.out]} onChange={v => set({ [k.out]: v })} ariaLabel="Cut time" /></div>
                    </div>
                    <div>
                        <Label>Break</Label>
                        <div className="flex items-center gap-1">
                            <button type="button" className={key(false, 'min-w-0 w-9 px-0')} onClick={() => set({ [k.brk]: Math.max(0, (Number(s[k.brk]) || 0) - 15) })} aria-label="Less break">−</button>
                            <span className="w-16 text-center font-mono text-[13px] font-bold tabular-nums">{Number(s[k.brk]) || 0}m</span>
                            <button type="button" className={key(false, 'min-w-0 w-9 px-0')} onClick={() => set({ [k.brk]: (Number(s[k.brk]) || 0) + 15 })} aria-label="More break">+</button>
                        </div>
                    </div>
                    <div>
                        <Label>Billable</Label>
                        <div className="h-9 flex items-center font-mono text-[15px] font-bold tabular-nums text-amber-300">{hours > 0 ? `${hours} hrs` : '—'}</div>
                        {calc.wrapped && <div className="font-mono text-[10px] text-amber-400 mt-0.5">cut is before start → counted as next day. Check AM/PM.</div>}
                    </div>
                </div>
            )}
        </div>
    </>
);

export default function QuickPricer({ onClose }) {
    const { config } = useStore();
    const { show } = useToast();
    const [s, setS] = useState(loadSaved);
    const set = (patch) => setS(prev => ({ ...prev, ...patch }));

    useEffect(() => { try { localStorage.setItem(LS_KEY, JSON.stringify(s)); } catch { /* ignore */ } }, [s]);

    // Rate auto-fills from the rate table whenever crew/truck change —
    // unless the user has typed their own (rateTouched), same as the
    // estimate form. Changing crew/truck resets the override.
    const rateInfo = useMemo(() => lookupRateInfo(config, s.crew, s.truck, 'moving'), [config, s.crew, s.truck]);
    const tableRate = rateInfo.rate;
    const tablePackRate = useMemo(() => lookupPackingRate(config, s.packers), [config, s.packers]);
    useEffect(() => { if (!s.rateTouched && tableRate > 0) set({ rate: tableRate }); }, [tableRate]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { if (!s.packRateTouched && tablePackRate > 0) set({ packRate: tablePackRate }); }, [tablePackRate]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Materials picker (Jaylen 2026-09-11: "we already have a material
    // price table used on the packing version of the crew portal… pop up
    // when you click into materials… hit save and it shows the total").
    // Same catalog (material_types, customer PRICE not cost) and the same
    // subtotal + sales-tax math as the crew portal's recap form.
    const [catalog, setCatalog] = useState(null);       // null = not loaded yet
    const [matOpen, setMatOpen] = useState(false);
    const [draftPicks, setDraftPicks] = useState({});
    const taxPct = useMemo(() => { const n = parseFloat(config?.materials_tax_rate); return Number.isFinite(n) ? n : 6.75; }, [config]);
    const openMaterials = async () => {
        setDraftPicks({ ...(s.materialsPicks || {}) });
        setMatOpen(true);
        if (catalog === null) {
            try { setCatalog(await db.listMaterialTypes()); } catch { setCatalog([]); }
        }
    };
    const matTotals = (picks, cat) => {
        const list = cat || [];
        const items = list.filter(m => (Number(picks?.[m.id]) || 0) > 0).map(m => ({ ...m, count: Number(picks[m.id]) || 0 }));
        const subtotal = Math.round(items.reduce((t, m) => t + m.count * (Number(m.price) || 0), 0) * 100) / 100;
        const tax = Math.round(subtotal * taxPct) / 100;
        return { items, subtotal, tax, total: Math.round((subtotal + tax) * 100) / 100 };
    };
    const draftTotals = matTotals(draftPicks, catalog);
    const saveMaterials = () => {
        const t = matTotals(draftPicks, catalog);
        set({ materialsPicks: draftPicks, materials: t.total ? String(t.total) : '' });
        setMatOpen(false);
    };
    const pickedItems = matTotals(s.materialsPicks, catalog).items;
    // Saved picks from a previous session need the catalog to show names —
    // load it on mount when there's anything to resolve.
    useEffect(() => {
        if (catalog !== null || !Object.values(s.materialsPicks || {}).some(n => Number(n) > 0)) return;
        db.listMaterialTypes().then(setCatalog).catch(() => setCatalog([]));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const heavyItemFee = useMemo(() => { try { return parseFloat(JSON.parse(config?.heavy_item_fee || '200')) || 200; } catch { return 200; } }, [config]);
    const extraFeePerHr = extraTruckFeePerHrFrom(config);
    const surcharge = s.waived ? 0 : s.extras.length * extraFeePerHr;
    const effRate = num(s.rate) + surcharge;
    // Clock math. A cut earlier than the start is treated as crossing
    // midnight (rare, but real on a long day) and FLAGGED — nine times out
    // of ten it's an AM/PM slip (Jaylen 2026-09-11: 2:15 PM → 4:45 AM read
    // as zero hours with no explanation).
    const calcClock = (inn, out, brk) => {
        const direct = computeDayHours(inn, out, brk);
        if (direct === null) return { hours: 0, wrapped: false };
        const [sh, sm] = String(inn).split(':').map(Number);
        const [eh, em] = String(out).split(':').map(Number);
        const span = (eh * 60 + em) - (sh * 60 + sm);
        if (span > 0) return { hours: direct, wrapped: false };
        const mins = span + 24 * 60 - (Number(brk) || 0);
        return { hours: mins > 0 ? Math.round((mins / 60) * 100) / 100 : 0, wrapped: true };
    };
    const clockCalc = s.hoursMode === 'clock' ? calcClock(s.clockIn, s.clockOut, s.breakMin) : { hours: 0, wrapped: false };
    const clockHours = clockCalc.hours;
    const minH = s.hoursMode === 'clock' ? clockHours : num(s.minHours);
    const maxH = s.hoursMode === 'clock' ? clockHours : (num(s.maxHours) || minH);
    // Packing gets the same two ways of stating hours (Jaylen 2026-09-11:
    // "match the Packing portion to the portion above").
    const packClockCalc = s.packing && s.packHoursMode === 'clock' ? calcClock(s.packClockIn, s.packClockOut, s.packBreakMin) : { hours: 0, wrapped: false };
    const packClockHours = packClockCalc.hours;
    const fmtClock = (t) => { const [h, m] = String(t || '').split(':').map(Number); if (Number.isNaN(h)) return t; const ap = h >= 12 ? 'PM' : 'AM'; return `${((h + 11) % 12) + 1}:${String(m || 0).padStart(2, '0')} ${ap}`; };
    const laborMin = minH * effRate;
    const laborMax = maxH * effRate;
    const pMin = !s.packing ? 0 : s.packHoursMode === 'clock' ? packClockHours : num(s.packMin);
    const pMax = !s.packing ? 0 : s.packHoursMode === 'clock' ? packClockHours : (num(s.packMax) || pMin);
    const packMinTotal = pMin * num(s.packRate);
    const packMaxTotal = pMax * num(s.packRate);
    const heavyTotal = (parseInt(s.heavy) || 0) * heavyItemFee;
    const addOns = num(s.travel) + num(s.hold) + num(s.materials) + heavyTotal + num(s.removal);
    const totalMin = laborMin + packMinTotal + addOns;
    const totalMax = laborMax + packMaxTotal + addOns;
    const isRange = totalMin !== totalMax && totalMax > 0;
    // Card processing fee — a percentage of the whole bill (labor, packing
    // and add-ons alike), rounded to the cent. 3% unless Settings carries
    // `card_fee_pct`. Shown only when the toggle is on.
    const cardPct = useMemo(() => { const n = parseFloat(config?.card_fee_pct); return Number.isFinite(n) && n > 0 ? n : 3; }, [config]);
    const cardFeeOf = (t) => Math.round(t * cardPct) / 100;
    const cardMin = cardFeeOf(totalMin);
    const cardMax = cardFeeOf(totalMax);

    const truckLabel = [truckSizeShortLabel(s.truck), ...s.extras.map(truckSizeShortLabel)].filter(t => t && t !== 'No Truck').join(' + ') || 'no truck';

    const summaryLines = () => {
        const lines = [];
        lines.push(`${s.crew} movers · ${truckLabel}`);
        if (s.hoursMode === 'clock' && clockHours > 0) lines.push(`Time: ${fmtClock(s.clockIn)} – ${fmtClock(s.clockOut)}${clockCalc.wrapped ? ' (next day)' : ''}${s.breakMin ? ` − ${s.breakMin} min break` : ''} = ${clockHours} hrs`);
        if (minH > 0) lines.push(`Labor: ${minH}${maxH !== minH ? `–${maxH}` : ''} hrs × ${money(effRate)}/hr${surcharge ? ` (incl. ${money(surcharge)}/hr extra truck)` : ''} = ${money(laborMin)}${laborMax !== laborMin ? ` – ${money(laborMax)}` : ''}`);
        if (s.packing && s.packHoursMode === 'clock' && packClockHours > 0) lines.push(`Packing time: ${fmtClock(s.packClockIn)} – ${fmtClock(s.packClockOut)}${packClockCalc.wrapped ? ' (next day)' : ''}${s.packBreakMin ? ` − ${s.packBreakMin} min break` : ''} = ${packClockHours} hrs`);
        if (s.packing && packMinTotal > 0) lines.push(`Packing: ${s.packers} packers · ${pMin}${pMax !== pMin ? `–${pMax}` : ''} hrs × ${money(num(s.packRate))}/hr = ${money(packMinTotal)}${packMaxTotal !== packMinTotal ? ` – ${money(packMaxTotal)}` : ''}`);
        if (num(s.travel)) lines.push(`Travel: ${money(num(s.travel))}`);
        if (num(s.hold)) lines.push(`Hold: ${money(num(s.hold))}`);
        if (num(s.materials)) {
            lines.push(`Materials: ${money(num(s.materials))}${pickedItems.length ? ` (incl. ${taxPct}% tax)` : ''}`);
            pickedItems.forEach(m => lines.push(`  ${m.count} × ${m.name} @ ${money(Number(m.price) || 0)}`));
        }
        if (heavyTotal) lines.push(`Heavy items: ${s.heavy} × ${money(heavyItemFee)} = ${money(heavyTotal)}`);
        if (num(s.removal)) lines.push(`Removal: ${money(num(s.removal))}`);
        if (totalMax > 0) {
            lines.push(`${isRange ? 'Estimated total' : 'Total'}: ${money(totalMin)}${isRange ? ` – ${money(totalMax)}` : ''}`);
            if (s.cardFee) {
                lines.push(`Card processing fee (${cardPct}%): ${money(cardMin)}${isRange ? ` – ${money(cardMax)}` : ''}`);
                lines.push(`Total if paid by card: ${money(totalMin + cardMin)}${isRange ? ` – ${money(totalMax + cardMax)}` : ''}`);
            }
        }
        else lines.push(s.hoursMode === 'clock' ? 'set start + cut times…' : 'pick crew + truck, then hours…');
        return lines;
    };
    const copySummary = async () => {
        try { await navigator.clipboard.writeText(summaryLines().join('\n')); show('Summary copied'); }
        catch { show('Copy failed — select the text and copy manually', 'error'); }
    };
    const reset = () => setS({ ...DEFAULTS, rate: tableRate || '', packRate: tablePackRate || '' });

    // ── Look: calculator + terminal (Jaylen 2026-09-11: "pick a unique UI
    // design as a guide… could see this widget showing up in a few places,
    // may even live in the top toolbar"; then "instead of the receipt…
    // plain text like Terminal, black background, red text"). The left
    // side is a charcoal "device" — key groups like a desk calculator,
    // inputs like a display; the right side is a black terminal readout in
    // red mono that prints the breakdown — the same text Copy sends.
    // Self-contained: paints its own background so it can drop into a
    // popover, a modal or a page without the host styling it. Brand red
    // is reserved for the one action (Copy) and the active-key underline.
    const lines = summaryLines();

    return (
        <div className="rounded-2xl bg-[#16181d] text-slate-100 shadow-xl ring-1 ring-black/40 overflow-hidden">
            {/* Device header */}
            <div className="flex items-center justify-between gap-3 px-4 md:px-5 pt-4 pb-3 border-b border-white/5">
                <div className="flex items-center gap-3">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-red-600 text-white font-mono text-sm font-bold shadow-[0_2px_0_#7f1d1d]">$</span>
                    <div>
                        <div className="font-lemonmilk text-[13px] tracking-wide text-white leading-none">BILLING CALCULATOR</div>
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    <button type="button" onClick={reset} className="font-mono text-[11px] uppercase tracking-wider text-slate-400 hover:text-white px-2 py-1 rounded transition-colors">Reset</button>
                    {onClose && <button type="button" onClick={onClose} className="font-mono text-[11px] text-slate-400 hover:text-white px-2 py-1 rounded transition-colors" aria-label="Close billing calculator">✕</button>}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_290px]">
                {/* ── Keypad side ── */}
                <div className="p-4 md:p-5 space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-x-8 gap-y-5">
                        <div>
                            <Label>Crew</Label>
                            <div className="flex flex-wrap gap-1.5">
                                {CREW_SIZES.map(n => (
                                    <button key={n} type="button" className={key(s.crew === n)} onClick={() => set({ crew: n, rateTouched: false })}>{n}</button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Label>Truck</Label>
                            <div className="flex flex-wrap gap-1.5">
                                {TRUCK_TYPE_OPTIONS_COMPACT.map(t => (
                                    <button key={t.value} type="button" className={key(s.truck === t.value)} onClick={() => set({ truck: t.value, rateTouched: false })}>{t.label}</button>
                                ))}
                                <span className="w-px h-9 bg-white/10 mx-1 hidden md:block" />
                                {TRUCK_EXTRA_SIZES.map(sz => (
                                    <button key={sz.value} type="button" className={key(false, 'text-slate-400')} onClick={() => set({ extras: [...s.extras, sz.value] })}>+{truckSizeShortLabel(sz.value)}</button>
                                ))}
                                {s.extras.map((t, i) => (
                                    <span key={i} className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-md bg-red-600/15 text-red-300 border border-red-500/40 text-[13px] font-bold">
                                        +{truckSizeShortLabel(t)}
                                        <button type="button" onClick={() => set({ extras: s.extras.filter((_, j) => j !== i) })} className="text-red-300/70 hover:text-white leading-none" aria-label="Remove extra truck">×</button>
                                    </span>
                                ))}
                            </div>
                            <Hint>
                                {s.extras.length
                                    ? <>extra truck +{money(extraFeePerHr)}/hr each on every billed hour · <button type="button" onClick={() => set({ waived: !s.waived })} className={`underline underline-offset-2 ${s.waived ? 'text-amber-300' : 'text-slate-400 hover:text-white'}`}>{s.waived ? 'fee waived — click to charge' : 'waive fee'}</button></>
                                    : <>extras add {money(extraFeePerHr)}/hr each</>}
                            </Hint>
                        </div>
                    </div>

                    {/* Rate + hours display row */}
                    <div className="rounded-xl bg-black/25 ring-1 ring-white/5 p-3 md:p-4">
                        <div className="grid grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[150px_auto_minmax(0,1fr)] gap-4 items-start">
                            <div>
                                <Label>Rate $/hr</Label>
                                <NumField step={5} value={s.rate || ''} onChange={v => set({ rate: v, rateTouched: true })} ariaLabel="hourly rate" />
                                {tableRate > 0 && num(s.rate) !== tableRate ? (
                                    <Hint>table says {money(tableRate)}</Hint>
                                ) : tableRate > 0 && rateInfo.exact ? null : tableRate > 0 ? (
                                    <div className="font-mono text-[10px] text-amber-400 mt-1">no row for {s.crew} crew + {truckSizeShortLabel(s.truck)} — borrowing the {truckSizeShortLabel(rateInfo.fallbackTruck)} rate. Add it in Settings → Rate Table.</div>
                                ) : (
                                    <div className="font-mono text-[10px] text-amber-400 mt-1">no rate for {s.crew} crew — type one, or add it in Settings → Rate Table.</div>
                                )}
                            </div>
                            <HoursControls s={s} set={set} k={{ mode: 'hoursMode', min: 'minHours', max: 'maxHours', in: 'clockIn', out: 'clockOut', brk: 'breakMin' }} calc={clockCalc} hours={clockHours} />
                        </div>
                    </div>

                    {/* Packing */}
                    <div className={`rounded-xl ring-1 p-3 md:p-4 transition-colors ${s.packing ? 'bg-amber-400/10 ring-amber-400/30' : 'bg-black/25 ring-white/5'}`}>
                        {/* Header line carries the toggle AND the packer count so the
                            grid below has the exact shape of the labor panel above
                            (Rate | Hours by | controls) and stays just as slim. */}
                        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                            <button type="button" onClick={() => set({ packing: !s.packing })} className="flex items-center gap-3 text-[13px] font-bold text-white">
                                <span className={`w-9 h-5 rounded-full relative transition-colors ${s.packing ? 'bg-amber-400' : 'bg-[#3a404c]'}`}>
                                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${s.packing ? 'left-4' : 'left-0.5'}`} />
                                </span>
                                Packing
                                {!s.packing && <span className="font-mono text-[10px] font-normal text-slate-500 tracking-wider uppercase">off</span>}
                            </button>
                            {s.packing && (
                                <div className="flex items-center gap-2">
                                    <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500">Packers</span>
                                    <div className="flex gap-1.5">
                                        {[2, 3, 4].map(n => <button key={n} type="button" className={key(s.packers === n)} onClick={() => set({ packers: n, packRateTouched: false })}>{n}</button>)}
                                    </div>
                                </div>
                            )}
                        </div>
                        {s.packing && (
                            <div className="grid grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[150px_auto_minmax(0,1fr)] gap-4 mt-4 items-start">
                                <div>
                                    <Label>Rate $/hr</Label>
                                    <NumField step={5} value={s.packRate || ''} onChange={v => set({ packRate: v, packRateTouched: true })} ariaLabel="packing rate" />
                                </div>
                                <HoursControls s={s} set={set} k={{ mode: 'packHoursMode', min: 'packMin', max: 'packMax', in: 'packClockIn', out: 'packClockOut', brk: 'packBreakMin' }} calc={packClockCalc} hours={packClockHours} minPh="2" maxPh="3" />
                            </div>
                        )}
                    </div>

                    {/* Add-ons */}
                    <div>
                        <Label>Add-ons</Label>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            {[['travel', 'Travel'], ['hold', 'Hold'], ['materials', 'Materials'], ['removal', 'Removal']].map(([k, label]) => (
                                <div key={k}>
                                    <div className="font-mono text-[10px] text-slate-400 mb-1">{label}{k === 'materials' && pickedItems.length > 0 && <span className="text-slate-600"> · {pickedItems.reduce((n, m) => n + m.count, 0)} items</span>}</div>
                                    <div className="relative">
                                        {k === 'materials' && <span className="absolute left-2.5 top-1/2 -translate-y-1/2 font-mono text-slate-500 text-[13px]">$</span>}
                                        {k === 'materials' ? (
                                            // Opens the catalog picker; the taxed total lives in the field.
                                            <button type="button" onClick={openMaterials} aria-expanded={matOpen}
                                                className={display + ' pl-6 text-left cursor-pointer hover:border-slate-500 ' + (matOpen ? 'border-red-500 ring-1 ring-red-500/40' : '')}>
                                                <span className="block text-right">{num(s.materials) ? num(s.materials).toFixed(2) : <span className="text-slate-600">0</span>}</span>
                                            </button>
                                        ) : (
                                            <NumField step={5} prefix="$" value={s[k]} onChange={v => set({ [k]: v })} ariaLabel={label} />
                                        )}
                                    </div>
                                </div>
                            ))}
                            <div>
                                <div className="font-mono text-[10px] text-slate-400 mb-1">Heavy items <span className="text-slate-600">· {money(heavyItemFee)} ea</span></div>
                                <NumField step={1} value={s.heavy || ''} onChange={v => set({ heavy: parseInt(v, 10) || 0 })} ariaLabel="heavy items" />
                            </div>
                        </div>
                        {matOpen && (
                            <div className="mt-3 rounded-xl bg-black/25 ring-1 ring-white/5 p-3 md:p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <Label>Materials · customer price · {taxPct}% tax</Label>
                                    <button type="button" onClick={() => setDraftPicks({})} className="font-mono text-[11px] uppercase tracking-wider text-slate-400 hover:text-white">Clear</button>
                                </div>
                                {catalog === null ? (
                                    <div className="font-mono text-[12px] text-slate-500">loading catalog…</div>
                                ) : catalog.length === 0 ? (
                                    <div className="font-mono text-[12px] text-slate-500">no materials in the catalog yet — add them in Settings.</div>
                                ) : (
                                    // Two columns (not three) so names get real width; names
                                    // wrap instead of truncating — "Picture Sleeve", not "Pict…".
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                                        {catalog.map(m => {
                                            const c = Number(draftPicks[m.id]) || 0;
                                            return (
                                                <div key={m.id} className={`flex items-center gap-2 py-1 border-b border-white/5 ${c > 0 ? 'text-white' : 'text-slate-400'}`}>
                                                    <div className="flex-1 min-w-0 pr-2">
                                                        <div className="text-[12px] font-semibold leading-tight">{m.name}</div>
                                                        <div className="font-mono text-[10px] text-slate-500">{money(Number(m.price) || 0)}{m.unit && m.unit !== 'each' ? ` / ${m.unit}` : ''}</div>
                                                    </div>
                                                    {/* Typed quantity (Jaylen 2026-09-11: "80 sleeves… clicking + 80
                                                        times isn't gonna work"). Select-all on focus so a tap and a
                                                        number replaces the old count. */}
                                                    <div className={`w-[9.5rem] flex-shrink-0 ${c > 0 ? '[&_input]:text-amber-300' : ''}`}>
                                                        <NumField step={1} value={c || ''} onChange={v => setDraftPicks(prev => ({ ...prev, [m.id]: Math.max(0, parseInt(v, 10) || 0) }))} ariaLabel={`${m.name} quantity`} />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap items-center justify-between gap-3">
                                    <div className="font-mono text-[12px] text-slate-300 tabular-nums">
                                        subtotal {money(draftTotals.subtotal)} · tax {money(draftTotals.tax)} · <span className="text-white font-bold">total {money(draftTotals.total)}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button type="button" onClick={() => setMatOpen(false)} className="h-9 px-4 rounded-md bg-[#272b34] hover:bg-[#323845] text-slate-200 text-[12px] font-bold">Cancel</button>
                                        <button type="button" onClick={saveMaterials} className="h-9 px-5 rounded-md bg-red-600 hover:bg-red-700 text-white text-[12px] font-bold uppercase tracking-wide">Save</button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Terminal side (Jaylen 2026-09-11: "plain text like
                    TextEdit or Terminal, black background, red text, fill the
                    whole square") ── The readout IS the copy text: what you
                    see is exactly what Copy puts on the clipboard. */}
                <div className="bg-black lg:border-l border-white/5 flex flex-col min-h-[280px]">
                    <pre className="flex-1 m-0 p-4 md:p-5 font-mono text-[17px] leading-7 text-red-500 whitespace-pre-wrap break-words overflow-auto selection:bg-red-500/30" aria-live="polite">
{lines.map((l, i) => <span key={i} className={totalMax > 0 && /^(Estimated total|Total)/.test(l) ? 'text-red-400 font-bold' : ''}>{l}{'\n'}</span>)}
<span className="inline-block w-[8px] h-[18px] bg-red-500 align-middle animate-pulse" aria-hidden="true" />
                    </pre>
                    <div className="px-4 md:px-5 pb-4 md:pb-5 flex items-center justify-between gap-3">
                        {/* Card-fee switch (2026-09-19) sits with the readout because it
                            changes what the readout says, not what the job costs. */}
                        <button type="button" onClick={() => set({ cardFee: !s.cardFee })} aria-pressed={s.cardFee}
                            className="flex items-center gap-2.5 text-[12px] font-bold text-slate-200 hover:text-white">
                            <span className={`w-9 h-5 rounded-full relative transition-colors ${s.cardFee ? 'bg-red-500' : 'bg-[#3a404c]'}`}>
                                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${s.cardFee ? 'left-4' : 'left-0.5'}`} />
                            </span>
                            <span>Card fee <span className="font-mono text-[10px] font-normal text-slate-400 tracking-wider uppercase">+{cardPct}%</span></span>
                        </button>
                        <button type="button" onClick={copySummary} disabled={totalMax === 0}
                            className="h-10 px-5 rounded-md bg-red-600 hover:bg-red-700 disabled:opacity-30 disabled:hover:bg-red-600 text-white font-mono text-[14px] font-bold tracking-wide uppercase transition-colors">
                            Copy
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
